import React, { useMemo, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { usePeProfitCalculations } from '../../hooks/usePeProfitCalculations';
import useAppStore from '../../store/index';
import { PEPnlAbweichung, PEPnlBlock, createDefaultPEProfitPrognose } from '../../types/peProfit';
import { useShallow } from 'zustand/react/shallow';
import BankerPitchReportSection from '../bankerPitch/BankerPitchReportSection';
import { generateAndPersistBankerPitch } from '../bankerPitch/generateAndPersistBankerPitch';
import { PE_PROFIT_LOGO_SRC } from './peProfitPrintLayout';
import { selectActivePeProfitWorkspace } from '../../store/peProfitWorkspace';
import { useCalculationsSelector } from '../../store/selectors/calculations';
import { formatMoney } from '../../utils/currencyFormatter';

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// HILFSFUNKTIONEN
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

type RowKey =
    | 'umsatzNetto'
    | 'wareneinsatz'
    | 'personalkosten'
    | 'betriebskosten'
    | 'geraetekosten'
    | 'sonstigeEinnahmen'
    | 'investitionen'
    | 'betriebsergebnis'
    | 'inhaberertrag'
    | 'rentabilitaetProzent';

interface PnlRowConfig {
    key: RowKey;
    label: string;
    isPercent?: boolean;
    isResult?: boolean;
    costRow?: boolean;
    incomeRow?: boolean;
}

const PNL_ROWS: PnlRowConfig[] = [
    { key: 'umsatzNetto', label: 'Umsatzerlöse (netto)' },
    { key: 'wareneinsatz', label: 'Wareneinsatz / Material', costRow: true },
    { key: 'personalkosten', label: 'Personalkosten', costRow: true },
    { key: 'betriebskosten', label: 'Betriebskosten', costRow: true },
    { key: 'geraetekosten', label: 'Gerätekosten', costRow: true },
    { key: 'sonstigeEinnahmen', label: 'Sonstige Einnahmen (Abschreibungen)', incomeRow: true },
    { key: 'investitionen', label: 'Investitionen', costRow: true },
    { key: 'betriebsergebnis', label: 'Betriebsergebnis (kalkulatorisch)', isResult: true },
    { key: 'inhaberertrag', label: 'Inhaberertrag (Gesamtposition)', isResult: true },
    { key: 'rentabilitaetProzent', label: 'Rentabilität', isPercent: true, isResult: true },
];

const getDisplayValues = (block: PEPnlBlock) => ({
    umsatzNetto: block.umsatzNetto,
    wareneinsatz: block.wareneinsatz,
    personalkosten: block.personalkostenProduktiv + block.personalkostenUnproduktiv,
    betriebskosten: block.betriebskosten,
    geraetekosten: block.geraetekosten,
    sonstigeEinnahmen: block.sonstigeEinnahmen ?? 0,
    investitionen: block.investitionen,
    betriebsergebnis: block.betriebsergebnis,
    inhaberertrag: block.inhaberertrag,
    rentabilitaetProzent: block.rentabilitaetProzent,
});

const getDisplayAbweichung = (abweichung: PEPnlAbweichung) => ({
    umsatzNetto: abweichung.umsatzNetto,
    wareneinsatz: abweichung.wareneinsatz,
    personalkosten: abweichung.personalkostenProduktiv + abweichung.personalkostenUnproduktiv,
    betriebskosten: abweichung.betriebskosten,
    geraetekosten: abweichung.geraetekosten,
    sonstigeEinnahmen: abweichung.sonstigeEinnahmen ?? 0,
    investitionen: abweichung.investitionen,
    betriebsergebnis: abweichung.betriebsergebnis,
    inhaberertrag: abweichung.inhaberertrag,
    rentabilitaetProzent: abweichung.rentabilitaetProzent,
});

const PNL_QUOTE_ROWS: RowKey[] = [
    'wareneinsatz',
    'personalkosten',
    'betriebskosten',
    'geraetekosten',
    'betriebsergebnis',
];

const formatEuro = (value: number, currencyCode: string = 'EUR'): string =>
    formatMoney(Math.round(Number(value) || 0), currencyCode, 0);

/** Bank-/BWA-Näherung aus den Report-Zeilen (AfA als Kosten, dann Cashflow +AfA). */
const calcBankKennzahlen = (
    display: ReturnType<typeof getDisplayValues>,
    einkommensteuer: number
) => {
    const kostenOhneAfA =
        display.wareneinsatz +
        display.personalkosten +
        display.betriebskosten +
        display.geraetekosten +
        display.investitionen;
    const abschreibungen = display.sonstigeEinnahmen;
    const betriebsergebnisVorSteuern = display.umsatzNetto - kostenOhneAfA - abschreibungen;
    const betriebsergebnisNachSteuern = betriebsergebnisVorSteuern - (einkommensteuer || 0);
    const cashflow = betriebsergebnisNachSteuern + abschreibungen;

    return {
        abschreibungen,
        einkommensteuer: einkommensteuer || 0,
        betriebsergebnisVorSteuern,
        betriebsergebnisNachSteuern,
        cashflow,
    };
};

const formatUmsatzQuote = (betrag: number, umsatz: number): string => {
    if (!umsatz) return '0,00 %';
    const pct = (Math.round(Number(betrag)) / Math.round(Number(umsatz))) * 100;
    return `${pct.toFixed(2).replace('.', ',')} %`;
};

const PnlCellValue: React.FC<{
    rowKey: RowKey;
    value: number;
    isPercent?: boolean;
    umsatz: number;
    tone: 'ist' | 'prognose';
    currencyCode?: string;
}> = ({ rowKey, value, isPercent, umsatz, tone, currencyCode = 'EUR' }) => {
    if (isPercent) {
        return <span className="whitespace-nowrap tabular-nums">{formatPercentValue(value)}</span>;
    }

    const euroClass = tone === 'prognose' ? 'text-slate-900' : 'text-slate-600';

    if (PNL_QUOTE_ROWS.includes(rowKey)) {
        return (
            <span className="inline-flex flex-col items-end gap-0.5 leading-tight">
                <span className={`whitespace-nowrap tabular-nums ${euroClass}`}>
                    {formatEuro(value, currencyCode)}
                </span>
                <span className="whitespace-nowrap text-xs font-medium tabular-nums text-[#9a7348]">
                    ({formatUmsatzQuote(value, umsatz)})
                </span>
            </span>
        );
    }

    return (
        <span className={`whitespace-nowrap tabular-nums ${euroClass}`}>
            {formatEuro(value, currencyCode)}
        </span>
    );
};

const formatPercentValue = (value: number): string => `${(value || 0).toFixed(2).replace('.', ',')} %`;

const getAbweichungClass = (key: RowKey, value: number, costRow?: boolean, incomeRow?: boolean): string => {
    if (value === 0) return 'text-slate-400';
    const isPositive = value > 0;

    if (incomeRow || key === 'sonstigeEinnahmen') {
        return isPositive ? 'text-emerald-700' : 'text-rose-700';
    }

    if (key === 'umsatzNetto' || key === 'betriebsergebnis' || key === 'inhaberertrag' || key === 'rentabilitaetProzent') {
        return isPositive ? 'text-emerald-700' : 'text-rose-700';
    }

    if (costRow) {
        return isPositive ? 'text-rose-700' : 'text-emerald-700';
    }

    return isPositive ? 'text-emerald-700' : 'text-rose-700';
};

const toEingabeFromMonat = (
    monat: number,
    ansichtsModus: 'monat' | 'jahr',
    zeitraum: number
): number => (ansichtsModus === 'jahr' ? Number(monat) * Number(zeitraum) : Number(monat));

const formatStoreMonatForAnsicht = (
    monat: number,
    ansichtsModus: 'monat' | 'jahr',
    zeitraum: number,
    formatCurrency: (value: number) => string
): string => formatCurrency(toEingabeFromMonat(Number(monat), ansichtsModus, zeitraum));

const DetailBlock: React.FC<{
    title: string;
    children: React.ReactNode;
}> = ({ title, children }) => (
    <div className="pe-profit-pdf-section pe-profit-pdf-avoid-break rounded-lg border border-slate-200 bg-slate-50 px-5 py-4">
        <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-[#9a7348]">{title}</h4>
        {children}
    </div>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode; notiz?: string }> = ({
    label,
    value,
    notiz,
}) => (
    <li className="border-b border-slate-200 px-1 py-3 last:border-b-0">
        <p className="pr-2 text-sm leading-snug text-slate-700">
            {label}
            {notiz ? (
                <span className="mt-0.5 block text-xs font-normal text-slate-500">Zweck: {notiz}</span>
            ) : null}
        </p>
        <div className="mt-1.5 pr-2 text-right">{value}</div>
    </li>
);

const DetailValueText: React.FC<{ children: React.ReactNode; subline?: React.ReactNode }> = ({
    children,
    subline,
}) => (
    <span className="inline-block max-w-full text-right font-mono text-sm tabular-nums leading-snug text-slate-900">
        <span className="block whitespace-nowrap">{children}</span>
        {subline ? (
            <span className="mt-0.5 block whitespace-nowrap text-xs font-sans text-slate-600">{subline}</span>
        ) : null}
    </span>
);

const DetailAnpassungValue: React.FC<{
    istMonat: number;
    zielMonat: number;
    periodLabel: string;
    formatValue: (monat: number) => string;
    suffix?: string;
}> = ({ istMonat, zielMonat, periodLabel, formatValue, suffix }) => {
    const delta = zielMonat - istMonat;

    if (delta === 0) {
        return (
            <DetailValueText subline={`/ ${periodLabel}`}>
                {formatValue(istMonat)}
            </DetailValueText>
        );
    }

    const prefix = delta > 0 ? '+' : '−';
    const deltaLine = `Δ ${prefix}${formatValue(Math.abs(delta))} / ${periodLabel}${suffix ? ` · ${suffix}` : ''}`;

    return (
        <DetailValueText subline={deltaLine}>
            {formatValue(istMonat)} → {formatValue(zielMonat)}
        </DetailValueText>
    );
};

// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++
// KOMPONENTE
// +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++

const PEProfitReport: React.FC<{
    showUnterschriftSection?: boolean;
    hideInlineBankerPitchButton?: boolean;
}> = ({
    showUnterschriftSection = true,
    hideInlineBankerPitchButton = false,
}) => {
    const [isGenerating, setIsGenerating] = useState(false);
    const peProfit = usePeProfitCalculations();
    const calc = useCalculationsSelector();
    const currencyCode = calc.currencyCode || 'EUR';
    const storeVersion = peProfit.storeVersion ?? 0;
    const {
        isPlanungsAnalyse,
        gemeinkostenDetailAnpassungen,
        personalAnpassungen,
        neueDienstleistungen,
        neueInvestitionen,
        ansichtsModus,
        preisAnpassungProzent,
        materialZielMonat,
        materialNotiz,
        bankerPitchActiveId,
        bankerPitchArchiv,
        clearActiveSzenarioBankerPitch,
    } = useAppStore(
        useShallow((state) => {
            const ws = selectActivePeProfitWorkspace(state);
            const isPeProfit = ws.workspace === 'peProfit';
            return {
                isPlanungsAnalyse: ws.workspace === 'planungsAnalyse',
                gemeinkostenDetailAnpassungen: ws.prognose?.gemeinkostenDetailAnpassungen,
                personalAnpassungen: ws.prognose?.personalAnpassungen,
                neueDienstleistungen: ws.prognose?.neueDienstleistungen,
                neueInvestitionen: ws.prognose?.neueInvestitionen,
                ansichtsModus: ws.prognose?.ansichtsModus ?? 'monat',
                preisAnpassungProzent: ws.prognose?.preisAnpassungProzent ?? 0,
                materialZielMonat: ws.prognose?.materialZielMonat ?? null,
                materialNotiz: ws.prognose?.materialNotiz ?? '',
                // Banker-Pitch nur im PE-Profit-Workspace
                bankerPitchActiveId: isPeProfit ? state.activeSzenarioId : null,
                bankerPitchArchiv: isPeProfit ? state.planungsArchiv ?? [] : [],
                clearActiveSzenarioBankerPitch: state.clearActiveSzenarioBankerPitch,
            };
        })
    );

    const bankerPitchSzenario = useMemo(
        () => bankerPitchArchiv.find((entry) => entry.id === bankerPitchActiveId) ?? null,
        [bankerPitchArchiv, bankerPitchActiveId]
    );
    const bankerPitchText = bankerPitchSzenario?.bankerPitchText?.trim() || '';
    const hasBankerPitch = Boolean(bankerPitchText) && !isPlanungsAnalyse;
    const isJahresAnsicht = peProfit.ansichtsModus === 'jahr';

    const handleGenerateBankerPitch = async () => {
        if (!bankerPitchActiveId) {
            window.alert(
                'Bitte zuerst ein Szenario aus dem Archiv öffnen oder eine neue Rentabilitätsplanung anlegen.'
            );
            return;
        }
        setIsGenerating(true);
        try {
            await generateAndPersistBankerPitch(peProfit);
        } catch (error) {
            console.error('generateBankerPitch fehlgeschlagen:', error);
            window.alert(
                error instanceof Error
                    ? error.message
                    : 'KI-Text konnte nicht generiert werden. Bitte später erneut versuchen.'
            );
        } finally {
            setIsGenerating(false);
        }
    };

    const handleClearBankerPitch = async () => {
        clearActiveSzenarioBankerPitch();
        try {
            const { persistClientDataImmediate } = await import('../../config/firebaseSync');
            await persistClientDataImmediate();
        } catch (error) {
            console.error('Banker-Pitch-Löschen Sync fehlgeschlagen:', error);
        }
    };
    const clientData = useAppStore((state) => state.clientData);
    const firmenname = clientData?.firmenname?.trim() || clientData?.unternehmername?.trim() || '';
    const reportDatum = new Date().toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    const unterschriftRaw = useAppStore((state) => state.peProfitPrognose?.unterschrift);
    const unterschrift = unterschriftRaw ?? createDefaultPEProfitPrognose().unterschrift;
    const updateUnterschrift = useAppStore((state) => state.updateUnterschrift);

    const formatCurrency = (value: number) => formatEuro(value, currencyCode);
    const formatPercent = formatPercentValue;
    const zeitraum = peProfit.zeitraum || 12;
    const periodLabel = ansichtsModus === 'jahr' ? 'Jahr' : 'Monat';

    const formatAnpassungWert = (monat: number): string =>
        formatStoreMonatForAnsicht(monat, ansichtsModus, zeitraum, formatCurrency);

    const renderAnpassungValue = (istMonat: number, zielMonat: number, suffix?: string) => (
        <DetailAnpassungValue
            istMonat={istMonat}
            zielMonat={zielMonat}
            periodLabel={periodLabel}
            formatValue={formatAnpassungWert}
            suffix={suffix}
        />
    );

    const aktiveDienstleistungen = (neueDienstleistungen ?? []).filter((dl) => dl.aktiv);
    const aktiveInvestitionen = (neueInvestitionen ?? []).filter((inv) => inv.aktiv);
    const aktivePersonalAnpassungen = (personalAnpassungen ?? []).filter((pa) => pa.aktiv);
    const gkListe = gemeinkostenDetailAnpassungen ?? [];

    const materialGeaendert =
        materialZielMonat !== null ||
        peProfit.meta.materialPrognoseMonat !== peProfit.meta.materialIstMonat;
    const preisGeaendert =
        preisAnpassungProzent !== 0 || peProfit.meta.zusaetzlicherUmsatzJahr !== 0;

    const hatPersonalUmsatzDynamik =
        (peProfit.meta.umsatzNeuMitarbeiterJahr ?? 0) > 0 ||
        (peProfit.meta.umsatzEntlassungVerlustJahr ?? 0) > 0;

    const hatDetailAufstellung =
        gkListe.length > 0 ||
        aktivePersonalAnpassungen.length > 0 ||
        aktiveDienstleistungen.length > 0 ||
        aktiveInvestitionen.length > 0 ||
        materialGeaendert ||
        preisGeaendert ||
        hatPersonalUmsatzDynamik;

    const istUmsatzMonat = peProfit.meta.umsatzJahr.ist / zeitraum;
    const prognoseUmsatzMonat = peProfit.meta.umsatzJahr.prognose / zeitraum;

    const istDisplay = getDisplayValues(peProfit.ist);
    const prognoseDisplay = getDisplayValues(peProfit.prognose);
    const abweichungDisplay = getDisplayAbweichung(peProfit.abweichung);

    const defaultUnternehmerName =
        clientData?.unternehmername?.trim() ||
        clientData?.firmenname?.trim() ||
        '';

    const unternehmerNameValue = unterschrift.unternehmerName || defaultUnternehmerName;

    const formatDelta = (
        key: RowKey,
        value: number,
        isPercent?: boolean,
        costRow?: boolean,
        incomeRow?: boolean,
        quoteContext?: { istValue: number; prognoseValue: number; istUmsatz: number; prognoseUmsatz: number }
    ) => {
        const prefix = value > 0 ? '+' : '';
        const formatted = isPercent ? formatPercent(value) : formatCurrency(value);

        if (key === 'betriebsergebnis' && quoteContext) {
            const istQuote =
                quoteContext.istUmsatz > 0
                    ? (quoteContext.istValue / quoteContext.istUmsatz) * 100
                    : 0;
            const prognoseQuote =
                quoteContext.prognoseUmsatz > 0
                    ? (quoteContext.prognoseValue / quoteContext.prognoseUmsatz) * 100
                    : 0;
            const deltaPp = prognoseQuote - istQuote;
            const ppPrefix = deltaPp > 0 ? '+' : '';
            const ppFormatted = `${ppPrefix}${deltaPp.toFixed(2).replace('.', ',')} %`;

            return (
                <span className="inline-flex flex-col items-end gap-0.5 leading-tight">
                    <span className={getAbweichungClass(key, value, costRow, incomeRow)}>
                        {prefix}
                        {formatted}
                    </span>
                    <span
                        className={`whitespace-nowrap text-xs font-medium tabular-nums ${getAbweichungClass(
                            key,
                            deltaPp,
                            costRow,
                            incomeRow
                        )}`}
                    >
                        ({ppFormatted})
                    </span>
                </span>
            );
        }

        return (
            <span className={getAbweichungClass(key, value, costRow, incomeRow)}>
                {prefix}
                {formatted}
            </span>
        );
    };

    if (!peProfit.ready) {
        return (
            <div className="pe-profit-document rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500 shadow-xl">
                BWA-Daten werden geladen …
            </div>
        );
    }

    return (
        <div className="pe-profit-document min-w-0 rounded-2xl border border-slate-200 bg-white px-6 py-8 text-slate-900 shadow-xl md:px-10 md:py-10 print:shadow-none">
            <div className="pe-profit-pdf-section">
                <header className="mb-8 flex flex-col gap-6 border-b-2 border-[#E6C299] pb-6 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-4">
                    <img
                        src={PE_PROFIT_LOGO_SRC}
                        alt="PreisExpert Logo"
                        className="h-14 w-auto max-w-[140px] object-contain"
                    />
                    <div>
                        <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">
                            PreisExpert
                        </p>
                        <h2 className="text-2xl font-bold text-slate-900 md:text-3xl">
                            <span className="italic text-[#E6C299]">PE</span>
                            <span className="font-bold text-slate-900">Profit</span> — Rentabilitätsvorschau
                        </h2>
                        {firmenname ? (
                            <p className="mt-1 text-base font-medium text-slate-700">{firmenname}</p>
                        ) : null}
                    </div>
                </div>
                <div className="text-left text-sm text-slate-600 sm:text-right">
                    <p>
                        Szenario:{' '}
                        <span className="font-semibold text-slate-900">{peProfit.szenarioName}</span>
                    </p>
                    <p>
                        Zeitraum:{' '}
                        {peProfit.ansichtsModus === 'jahr' ? 'Jahresnetto' : 'Monatsnetto'} ({peProfit.zeitraum}{' '}
                        Monate)
                    </p>
                    <p>Erstellt am: {reportDatum}</p>
                </div>
            </header>

            <p className="mb-0 text-sm leading-relaxed text-slate-600">
                {isPlanungsAnalyse
                    ? 'Gegenüberstellung von Ist-Werten (BWA) und unternehmerischer Prognose — Fokus auf Kostenstruktur und Unternehmensentwicklung.'
                    : 'Gegenüberstellung von Ist-Werten (BWA) und unternehmerischer Prognose — bankkonforme Nachvollziehbarkeit aller Annahmen.'}
            </p>

            {!hideInlineBankerPitchButton && !isPlanungsAnalyse && (
                <div className="pe-profit-no-print mt-5 flex flex-col gap-1">
                    <button
                        type="button"
                        onClick={() => void handleGenerateBankerPitch()}
                        disabled={hasBankerPitch || isGenerating}
                        className="inline-flex w-fit items-center justify-center gap-2 rounded-xl bg-gradient-to-b from-[#E6C299] to-[#b48a56] px-5 py-3 text-sm font-black uppercase tracking-widest text-black shadow-[0_0_20px_rgba(230,194,153,0.25)] hover:from-[#f0d3b3] hover:to-[#cfa674] disabled:cursor-default disabled:opacity-70"
                    >
                        {isGenerating ? (
                            <Loader2 size={17} className="animate-spin" />
                        ) : (
                            <Sparkles size={17} />
                        )}
                        {isGenerating ? 'KI generiert...' : 'KI-Text für die Bank generieren'}
                    </button>
                    {!isJahresAnsicht && !hasBankerPitch ? (
                        <p className="text-[11px] text-slate-500">
                            Schaltet für die Generierung automatisch auf Jahresansicht um
                        </p>
                    ) : null}
                </div>
            )}

            <div className="mt-8 overflow-hidden rounded-xl border border-slate-200">
                <table key={`pnl-${storeVersion}`} className="w-full table-fixed border-collapse text-sm md:text-base">
                    <colgroup>
                        <col className="w-[32%]" />
                        <col className="w-[23%]" />
                        <col className="w-[23%]" />
                        <col className="w-[22%]" />
                    </colgroup>
                    <thead>
                        <tr className="border-b border-slate-200 bg-slate-100">
                            <th className="px-4 py-3 pr-5 text-left text-xs font-bold uppercase tracking-wide text-slate-700">
                                Position
                            </th>
                            <th className="px-3 py-3 pr-5 text-right text-xs font-bold uppercase tracking-wide text-slate-600">
                                Ist
                            </th>
                            <th className="px-3 py-3 pr-5 text-right text-xs font-bold uppercase tracking-wide text-slate-600">
                                Prognose
                            </th>
                            <th className="px-3 py-3 pr-5 text-right text-xs font-bold uppercase tracking-wide text-slate-600">
                                Abweichung
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {PNL_ROWS.map((row) => {
                            const istValue = istDisplay[row.key];
                            const prognoseValue = prognoseDisplay[row.key];
                            const deltaValue = abweichungDisplay[row.key];
                            const rowClass = row.isResult
                                ? 'border-t-2 border-slate-300 bg-slate-50 font-semibold'
                                : 'border-b border-slate-100';

                            return (
                                <tr key={row.key} className={rowClass}>
                                    <td className="px-4 py-3 pr-5 leading-snug text-slate-800">{row.label}</td>
                                    <td className="px-3 py-3 pr-5 text-right font-mono">
                                        <PnlCellValue
                                            rowKey={row.key}
                                            value={istValue}
                                            isPercent={row.isPercent}
                                            umsatz={istDisplay.umsatzNetto}
                                            tone="ist"
                                            currencyCode={currencyCode}
                                        />
                                    </td>
                                    <td className="px-3 py-3 pr-5 text-right font-mono">
                                        <PnlCellValue
                                            rowKey={row.key}
                                            value={prognoseValue}
                                            isPercent={row.isPercent}
                                            umsatz={prognoseDisplay.umsatzNetto}
                                            tone="prognose"
                                            currencyCode={currencyCode}
                                        />
                                    </td>
                                    <td className="px-3 py-3 pr-5 text-right font-mono text-sm tabular-nums md:text-base">
                                        {formatDelta(
                                            row.key,
                                            deltaValue,
                                            row.isPercent,
                                            row.costRow,
                                            row.incomeRow,
                                            row.key === 'betriebsergebnis'
                                                ? {
                                                      istValue: istValue,
                                                      prognoseValue: prognoseValue,
                                                      istUmsatz: istDisplay.umsatzNetto,
                                                      prognoseUmsatz: prognoseDisplay.umsatzNetto,
                                                  }
                                                : undefined
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {!isPlanungsAnalyse && (
            <div className="mt-4 space-y-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-600">
                <p>
                    <span className="font-semibold text-slate-700">
                        Hinweis für Banken &amp; Kreditsachbearbeitung — zwei Berechnungsarten:
                    </span>{' '}
                    Hier treffen zwei betriebswirtschaftlich unterschiedliche Sichtweisen aufeinander. Das in diesem
                    Report ausgewiesene <span className="font-medium text-slate-700">Betriebsergebnis (kalkulatorisch)</span>{' '}
                    folgt der PreisExpert-Kalkulation und ist bewusst{' '}
                    <span className="font-medium text-slate-700">nicht</span> identisch mit einer klassischen
                    Steuerberater-BWA- bzw. GuV-Näherung „Umsatz − Kosten einschließlich Abschreibungen“.
                </p>
                <div className="grid gap-2 md:grid-cols-2">
                    <div className="rounded-md border border-emerald-200/80 bg-emerald-50/60 px-3 py-2">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-emerald-800">
                            PreisExpert (diese Auswertung)
                        </div>
                        <p>
                            <span className="font-medium text-slate-700">Abschreibungen</span> werden kalkulatorisch als{' '}
                            <span className="font-medium text-slate-700">positiver Posten / sonstige Einnahmen</span>{' '}
                            geführt. Sie mindern den steuerlichen Gewinn, sind aber{' '}
                            <span className="font-medium text-slate-700">keine echten Auszahlungen</span> — der
                            Liquiditätsabfluss ist bereits bei der Investition entstanden. Deshalb werden sie hier
                            nicht wie laufende Betriebskosten vom Umsatz abgezogen.
                        </p>
                    </div>
                    <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
                        <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            Steuerberater-BWA / klassische GuV
                        </div>
                        <p>
                            Dort werden Abschreibungen als <span className="font-medium text-slate-700">Kosten</span>{' '}
                            gebucht, damit sie den Gewinn steuerlich mindern. Wer Umsatz abzüglich „Kosten
                            einschließlich Abschreibungen“ rechnet, erhält deshalb einen{' '}
                            <span className="font-medium text-slate-700">anderen</span> Ergebniswert als in dieser
                            kalkulatorischen Auswertung — das ist kein Rechenfehler, sondern eine andere Systematik.
                        </p>
                    </div>
                </div>
                <p>
                    Zusätzlich enthält der PreisExpert-Sollkostenblock die{' '}
                    <span className="font-medium text-slate-700">kalkulatorische Unternehmervergütung</span> und die{' '}
                    <span className="font-medium text-slate-700">angesetzte Einkommensteuer auf den Inhaberbedarf</span>
                    . Auch das unterscheidet das Ergebnis von einer reinen GuV-Zeile vor Privatentnahmen.
                </p>
                {(() => {
                    const u = istDisplay.umsatzNetto;
                    const kostenOhneAfA =
                        istDisplay.wareneinsatz +
                        istDisplay.personalkosten +
                        istDisplay.betriebskosten +
                        istDisplay.geraetekosten +
                        istDisplay.investitionen;
                    const afa = istDisplay.sonstigeEinnahmen;
                    const nachKostenzeilen = u - kostenOhneAfA;
                    const bwaNaeherung = nachKostenzeilen - afa;
                    const peZeilenlogik = nachKostenzeilen + afa;
                    const peBe = istDisplay.betriebsergebnis;
                    const sollblockAnpassung = peBe - peZeilenlogik;
                    const est = peProfit.ist.einkommensteuer || 0;
                    return (
                        <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                            <div className="border-b border-slate-100 bg-slate-100/80 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                Zahlenbrücke Ist ({periodLabel}) — Nachvollzug beider Sichtweisen
                            </div>
                            <table className="w-full text-left text-[11px]">
                                <tbody>
                                    <tr className="border-b border-slate-100">
                                        <td className="px-3 py-1.5 text-slate-600">
                                            Umsatzerlöse − ausgewiesene Kostenzeilen (ohne Abschreibungen)
                                        </td>
                                        <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                            {formatCurrency(nachKostenzeilen)}
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                        <td className="px-3 py-1.5 text-slate-600">
                                            Steuerberater-BWA / Bank-Näherung: Abschreibungen als Kosten (−)
                                        </td>
                                        <td className="px-3 py-1.5 text-right font-mono font-semibold text-slate-800">
                                            {formatCurrency(bwaNaeherung)}
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                        <td className="px-3 py-1.5 text-slate-600">
                                            PreisExpert-Kalkulation: Abschreibungen als sonstige Einnahmen (+)
                                        </td>
                                        <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                            {formatCurrency(peZeilenlogik)}
                                        </td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                        <td className="px-3 py-1.5 text-slate-600">
                                            Weitere PE-Sollkosten (u. a. kalk.&nbsp;ESt
                                            {est > 0 ? ` ${formatCurrency(est)}` : ''}
                                            {Math.abs(sollblockAnpassung - (est > 0 ? -est : 0)) > 1
                                                ? ', Azubi-Anteil o. ä.'
                                                : ''}
                                            )
                                        </td>
                                        <td className="px-3 py-1.5 text-right font-mono text-slate-500">
                                            {formatCurrency(sollblockAnpassung)}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="px-3 py-1.5 font-semibold text-slate-800">
                                            = ausgewiesenes Betriebsergebnis (kalkulatorisch)
                                        </td>
                                        <td className="px-3 py-1.5 text-right font-mono font-bold text-slate-900">
                                            {formatCurrency(peBe)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    );
                })()}
                {(() => {
                    const peBe = istDisplay.betriebsergebnis;
                    const est = peProfit.ist.einkommensteuer || 0;
                    const inhaberErtrag = istDisplay.inhaberertrag;
                    // Herleitung: BE + Unternehmerbedarf + ESt = Inhaberertrag
                    const unternehmerBedarf = inhaberErtrag - peBe - est;
                    return (
                        <div className="space-y-2">
                            <p>
                                <span className="font-semibold text-slate-700">
                                    Was bedeutet „Inhaberertrag (Gesamtposition)“?
                                </span>{' '}
                                Das ist <span className="font-medium text-slate-700">kein</span> GuV-Posten und{' '}
                                <span className="font-medium text-slate-700">kein</span> steuerlicher Gewinn. Es ist die
                                kalkulatorische <span className="font-medium text-slate-700">wirtschaftliche
                                Gesamtposition des Inhabers</span> im betrachteten Zeitraum.
                            </p>
                            <p>
                                Im Betriebsergebnis wurden zuvor die{' '}
                                <span className="font-medium text-slate-700">kalkulatorische Unternehmervergütung</span>{' '}
                                (privater Lebens-/Entnahmebedarf) und die{' '}
                                <span className="font-medium text-slate-700">angesetzte Einkommensteuer</span> als
                                Sollkosten berücksichtigt — sie „fehlen“ dort also bewusst. Beim Inhaberertrag werden
                                genau diese beiden Beträge wieder hinzugerechnet, weil sie wirtschaftlich dem Inhaber
                                zuzuordnen sind (Lebenshaltung bzw. Steuerreserve), auch wenn sie kein operativer
                                Betriebsüberschuss im engeren Sinne sind.
                            </p>
                            <div className="overflow-hidden rounded-md border border-slate-200 bg-white">
                                <div className="border-b border-slate-100 bg-slate-100/80 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Herleitung Inhaberertrag Ist ({periodLabel})
                                </div>
                                <table className="w-full text-left text-[11px]">
                                    <tbody>
                                        <tr className="border-b border-slate-100">
                                            <td className="px-3 py-1.5 text-slate-600">
                                                Betriebsergebnis (kalkulatorisch)
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(peBe)}
                                            </td>
                                        </tr>
                                        <tr className="border-b border-slate-100">
                                            <td className="px-3 py-1.5 text-slate-600">
                                                + kalkulatorische Unternehmervergütung (Inhaberbedarf / Entnahme)
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(unternehmerBedarf)}
                                            </td>
                                        </tr>
                                        <tr className="border-b border-slate-100">
                                            <td className="px-3 py-1.5 text-slate-600">
                                                + angesetzte Einkommensteuer auf den Inhaberbedarf
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(est)}
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="px-3 py-1.5 font-semibold text-slate-800">
                                                = Inhaberertrag (wirtschaftliche Gesamtposition)
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono font-bold text-slate-900">
                                                {formatCurrency(inhaberErtrag)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-[11px] text-slate-500">
                                Die darunter ausgewiesene <span className="font-medium text-slate-600">Rentabilität</span>{' '}
                                bezieht sich auf diesen Inhaberertrag im Verhältnis zum Netto-Umsatz — nicht auf ein
                                klassisches GuV-Betriebsergebnis.
                                {peProfit.ansichtsModus !== 'monat' ? null : (
                                    <>
                                        {' '}
                                        Bei reiner Umsatzanpassung ohne Cockpit-Kostenänderungen gilt:{' '}
                                        <span className="font-medium text-slate-600">
                                            Prognose-Betriebsergebnis = Ist-Betriebsergebnis + Δ Umsatz
                                        </span>
                                        .
                                    </>
                                )}
                            </p>
                        </div>
                    );
                })()}

                {(() => {
                    const istBank = calcBankKennzahlen(
                        istDisplay,
                        peProfit.ist.einkommensteuer || 0
                    );
                    const prognoseBank = calcBankKennzahlen(
                        prognoseDisplay,
                        peProfit.prognose.einkommensteuer || 0
                    );
                    return (
                        <div className="pe-profit-pdf-avoid-break space-y-2 rounded-md border border-slate-300 bg-white px-3 py-3">
                            <p>
                                <span className="font-semibold text-slate-700">
                                    Bankseitige Kennzahlen (BWA-/GuV-Näherung):
                                </span>{' '}
                                Für die Kreditprüfung sind insbesondere das{' '}
                                <span className="font-medium text-slate-700">Betriebsergebnis nach Steuern</span> und der
                                daraus abgeleitete{' '}
                                <span className="font-medium text-slate-700">
                                    Cashflow (BE nach Steuern + Abschreibungen)
                                </span>{' '}
                                relevant — daraus müssen u. a. künftige Zins- und Tilgungsverpflichtungen bedient
                                werden. Diese Werte folgen der klassischen Bank-/Steuerberater-Logik (Abschreibungen
                                als Kosten) und sind von der PE-Zeile „Betriebsergebnis (kalkulatorisch)“ zu
                                unterscheiden.
                            </p>
                            <div className="overflow-hidden rounded-md border border-slate-200">
                                <table className="w-full text-left text-[11px]">
                                    <thead>
                                        <tr className="border-b border-slate-100 bg-slate-100/80 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            <th className="px-3 py-2 font-bold">Herleitung ({periodLabel})</th>
                                            <th className="px-3 py-2 text-right font-bold">Ist</th>
                                            <th className="px-3 py-2 text-right font-bold">Prognose</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr className="border-b border-slate-100">
                                            <td className="px-3 py-1.5 text-slate-600">
                                                Betriebsergebnis vor Steuern
                                                <span className="block text-[10px] text-slate-400">
                                                    Umsatz − Kostenzeilen − Abschreibungen als Aufwand
                                                </span>
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(istBank.betriebsergebnisVorSteuern)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(prognoseBank.betriebsergebnisVorSteuern)}
                                            </td>
                                        </tr>
                                        <tr className="border-b border-slate-100">
                                            <td className="px-3 py-1.5 text-slate-600">
                                                − angesetzte Einkommensteuer (PE)
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(istBank.einkommensteuer)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(prognoseBank.einkommensteuer)}
                                            </td>
                                        </tr>
                                        <tr className="border-b border-slate-100 bg-slate-50/80">
                                            <td className="px-3 py-1.5 font-semibold text-slate-800">
                                                = Betriebsergebnis nach Steuern
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono font-bold text-slate-900">
                                                {formatCurrency(istBank.betriebsergebnisNachSteuern)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono font-bold text-slate-900">
                                                {formatCurrency(prognoseBank.betriebsergebnisNachSteuern)}
                                            </td>
                                        </tr>
                                        <tr className="border-b border-slate-100">
                                            <td className="px-3 py-1.5 text-slate-600">
                                                + Abschreibungen / sonstige Pluswerte
                                                <span className="block text-[10px] text-slate-400">
                                                    nicht zahlungswirksam — wieder hinzugerechnet
                                                </span>
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(istBank.abschreibungen)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono text-slate-800">
                                                {formatCurrency(prognoseBank.abschreibungen)}
                                            </td>
                                        </tr>
                                        <tr className="bg-emerald-50/70">
                                            <td className="px-3 py-1.5 font-semibold text-emerald-900">
                                                = Cashflow (Schuldendienstfähigkeit)
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-900">
                                                {formatCurrency(istBank.cashflow)}
                                            </td>
                                            <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-900">
                                                {formatCurrency(prognoseBank.cashflow)}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            <p className="text-[11px] text-slate-500">
                                Hinweis: Die Kostenzeilen enthalten die in PreisExpert erfasste{' '}
                                <span className="font-medium text-slate-600">kalkulatorische Unternehmervergütung</span>.
                                Die Steuer ist die im System angesetzte ESt auf den Inhaberbedarf — keine
                                vollständige persönliche Steuerveranlagung. Der Cashflow ist eine Näherung zur
                                Beurteilung der Zins-/Tilgungsfähigkeit, kein vollständiger Finanzplan.
                            </p>
                        </div>
                    );
                })()}
            </div>
            )}
            </div>

            {hatDetailAufstellung && (
                <section
                    key={`detail-${storeVersion}`}
                    className="mt-8 rounded-xl border border-slate-200 bg-white px-2 py-6 md:px-4"
                >
                    <div className="pe-profit-pdf-section mb-5 px-1">
                        <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-[#9a7348]">
                            Detail-Aufstellung der Prognose-Anpassungen
                        </h3>
                        <p className="text-sm text-slate-600">
                            Nachvollziehbare Zusammensetzung der Prognose — alle Werte in {periodLabel}netto.
                            Einzelposten mit Δ zeigen die Abweichung vom erfassten IST-Wert.
                        </p>
                    </div>

                    <div className="pe-profit-detail-grid grid gap-4 lg:grid-cols-2">
                        {preisGeaendert && (
                            <DetailBlock title="Preisanpassung & Umsatz">
                                <ul>
                                    {preisAnpassungProzent !== 0 && (
                                        <DetailRow
                                            label="Preisanpassung"
                                            value={
                                                <DetailValueText subline="auf Basisumsatz">
                                                    {formatPercent(preisAnpassungProzent)}
                                                </DetailValueText>
                                            }
                                        />
                                    )}
                                    {peProfit.meta.kundenVerlustProzent > 0 && (
                                        <DetailRow
                                            label="Geschätzter Kundenschwund"
                                            value={<DetailValueText>{formatPercent(peProfit.meta.kundenVerlustProzent)}</DetailValueText>}
                                        />
                                    )}
                                    <DetailRow
                                        label="Umsatzerlöse (netto)"
                                        value={renderAnpassungValue(istUmsatzMonat, prognoseUmsatzMonat)}
                                    />
                                    {(peProfit.meta.umsatzNeuMitarbeiterJahr ?? 0) > 0 && (
                                        <DetailRow
                                            label="Zusatzumsatz neue Mitarbeiter"
                                            value={
                                                <DetailValueText subline="/ Jahr">
                                                    +{formatCurrency(peProfit.meta.umsatzNeuMitarbeiterJahr ?? 0)}
                                                </DetailValueText>
                                            }
                                        />
                                    )}
                                    {(peProfit.meta.umsatzEntlassungVerlustJahr ?? 0) > 0 && (
                                        <DetailRow
                                            label="Umsatzverlust durch Entlassungen"
                                            value={
                                                <DetailValueText subline="/ Jahr">
                                                    −{formatCurrency(peProfit.meta.umsatzEntlassungVerlustJahr ?? 0)}
                                                </DetailValueText>
                                            }
                                        />
                                    )}
                                </ul>
                            </DetailBlock>
                        )}

                        {materialGeaendert && (
                            <DetailBlock title="Wareneinsatz / Material">
                                <ul>
                                    <DetailRow
                                        label="Materialkosten gesamt"
                                        value={renderAnpassungValue(
                                            peProfit.meta.materialIstMonat,
                                            peProfit.meta.materialPrognoseMonat
                                        )}
                                        notiz={materialNotiz.trim() || undefined}
                                    />
                                    {(peProfit.meta.materialZusatzNeuMitarbeiterMonat ?? 0) > 0 && (
                                        <DetailRow
                                            label="Materialzuschlag (neue Mitarbeiter, IST-Quote)"
                                            value={
                                                <DetailValueText subline={`/ ${periodLabel}`}>
                                                    +{formatAnpassungWert(peProfit.meta.materialZusatzNeuMitarbeiterMonat ?? 0)}
                                                </DetailValueText>
                                            }
                                        />
                                    )}
                                </ul>
                            </DetailBlock>
                        )}

                        {gkListe.length > 0 && (
                            <DetailBlock title="Geänderte Betriebskosten">
                                <ul>
                                    {gkListe.length > 1 && (
                                        <DetailRow
                                            label="Betriebskosten gesamt (BWA)"
                                            value={renderAnpassungValue(
                                                peProfit.meta.betriebskostenIstMonat,
                                                peProfit.meta.betriebskostenPrognoseMonat
                                            )}
                                        />
                                    )}
                                    {gkListe.map((gk) => (
                                        <DetailRow
                                            key={gk.id}
                                            label={gk.label}
                                            value={renderAnpassungValue(gk.istWert, gk.neuerZielwert)}
                                            notiz={gk.notiz}
                                        />
                                    ))}
                                </ul>
                            </DetailBlock>
                        )}

                        {aktivePersonalAnpassungen.length > 0 && (
                            <DetailBlock title="Geänderte Personalkosten">
                                <ul>
                                    {aktivePersonalAnpassungen.length > 1 && (
                                        <DetailRow
                                            label="Personalkosten gesamt"
                                            value={renderAnpassungValue(
                                                peProfit.meta.personalkostenIstMonat,
                                                peProfit.meta.personalkostenPrognoseMonat
                                            )}
                                        />
                                    )}
                                    {aktivePersonalAnpassungen.map((pa) => (
                                        <DetailRow
                                            key={pa.id}
                                            label={
                                                pa.entlassen
                                                    ? `${pa.mitarbeiterName || 'Mitarbeiter'} (entlassen)`
                                                    : pa.mitarbeiterName || 'Mitarbeiter'
                                            }
                                            value={
                                                pa.entlassen
                                                    ? renderAnpassungValue(
                                                          pa.istWert,
                                                          0,
                                                          `Auffang ${(pa.umsatzAuffangProzent ?? 100).toFixed(0)} %`
                                                      )
                                                    : renderAnpassungValue(pa.istWert, pa.neuerZielwert)
                                            }
                                        />
                                    ))}
                                </ul>
                            </DetailBlock>
                        )}

                        {aktiveDienstleistungen.length > 0 && (
                            <DetailBlock title="Neue Dienstleistungen">
                                <ul>
                                    {aktiveDienstleistungen.map((dl) => (
                                        <DetailRow
                                            key={dl.id}
                                            label={dl.name || 'Neue Dienstleistung'}
                                            value={
                                                <DetailValueText subline={`/ ${periodLabel}`}>
                                                    {formatCurrency(
                                                        Number(
                                                            ansichtsModus === 'jahr'
                                                                ? dl.jahresumsatzNetto
                                                                : dl.jahresumsatzNetto / zeitraum
                                                        )
                                                    )}
                                                </DetailValueText>
                                            }
                                        />
                                    ))}
                                </ul>
                            </DetailBlock>
                        )}

                        {aktiveInvestitionen.length > 0 && (
                            <DetailBlock title="Neue Investitionen">
                                <ul>
                                    {aktiveInvestitionen.map((inv) => (
                                        <DetailRow
                                            key={inv.id}
                                            label={inv.name || 'Investition'}
                                            value={
                                                <DetailValueText subline={`/ ${periodLabel}`}>
                                                    {formatCurrency(
                                                        Number(
                                                            ansichtsModus === 'jahr'
                                                                ? inv.monatskosten * zeitraum
                                                                : inv.monatskosten
                                                        )
                                                    )}
                                                </DetailValueText>
                                            }
                                        />
                                    ))}
                                </ul>
                            </DetailBlock>
                        )}
                    </div>
                </section>
            )}

            <div className="pe-profit-print-meta pe-profit-pdf-section pe-profit-pdf-avoid-break mt-10 rounded-lg border border-slate-200 bg-slate-50 px-5 py-4 text-sm text-slate-600">
                <p>
                    Materialquote (Ist): {formatPercent(peProfit.ist.materialquoteProzent)} · Zusatzumsatz Prognose/Jahr:{' '}
                    {formatCurrency(peProfit.meta.zusaetzlicherUmsatzJahr)}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                    Berechnungsstand: {storeVersion} · Ansicht: {periodLabel}netto ({zeitraum} Monate/Jahr)
                </p>
            </div>

            <footer className="pe-profit-pdf-section mt-8 flex items-center justify-between border-t border-slate-200 pt-5 text-xs text-slate-500">
                <span>Erstellt mit PreisExpert PE Profit</span>
                <span>www.preis-expert.de</span>
            </footer>

            {showUnterschriftSection && (
            <section className="pe-profit-pdf-section mt-8 rounded-xl border border-slate-200 bg-slate-50 p-6 md:p-7">
                <h3 className="mb-4 text-xs font-bold uppercase tracking-[0.18em] text-[#9a7348]">
                    Verantwortungsabgrenzung &amp; Unterschrift
                </h3>

                <p className="mb-6 text-sm leading-relaxed text-slate-600">
                    Die vorstehende Rentabilitätsvorschau basiert auf den vom Unternehmer bereitgestellten Ist-Daten
                    und den von ihm selbst eingegebenen Prognoseannahmen. PreisExpert übernimmt keine Gewähr für die
                    Richtigkeit der Prognose oder deren Eignung für Kreditentscheidungen Dritter.
                </p>

                <div className="mb-6 grid gap-4 sm:grid-cols-3">
                    <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ort</span>
                        <input
                            type="text"
                            value={unterschrift.ort}
                            onChange={(e) => updateUnterschrift({ ort: e.target.value })}
                            className="pe-profit-print-input w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#E6C299]"
                            placeholder="z. B. München"
                        />
                        <span className="pe-profit-print-value hidden text-sm text-black">{unterschrift.ort || '—'}</span>
                    </label>

                    <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">Datum</span>
                        <input
                            type="date"
                            value={unterschrift.datum}
                            onChange={(e) => updateUnterschrift({ datum: e.target.value })}
                            className="pe-profit-print-input w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#E6C299]"
                        />
                        <span className="pe-profit-print-value hidden text-sm text-black">
                            {unterschrift.datum || '—'}
                        </span>
                    </label>

                    <label className="block">
                        <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                            Unternehmer
                        </span>
                        <input
                            type="text"
                            value={unternehmerNameValue}
                            onChange={(e) => updateUnterschrift({ unternehmerName: e.target.value })}
                            className="pe-profit-print-input w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-[#E6C299]"
                            placeholder="Name des Unternehmers"
                        />
                        <span className="pe-profit-print-value hidden text-sm text-black">
                            {unternehmerNameValue || '—'}
                        </span>
                    </label>
                </div>

                <div className="mb-6">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
                        Unterschrift Unternehmer
                    </div>
                    <div className="h-px w-full max-w-md border-b border-dashed border-slate-400" />
                    <p className="mt-2 text-sm text-slate-700">{unternehmerNameValue || '—'}</p>
                </div>

                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 bg-white p-4 pe-profit-print-input">
                    <input
                        type="checkbox"
                        checked={unterschrift.bestaetigt}
                        onChange={(e) => updateUnterschrift({ bestaetigt: e.target.checked })}
                        className="mt-0.5 h-4 w-4 rounded border-slate-300 text-[#b48a56] focus:ring-[#E6C299]/40"
                    />
                    <span className="text-sm text-slate-700">
                        Ich bestätige, dass die Prognosewerte auf meiner eigenen Einschätzung beruhen.
                    </span>
                </label>
                <p className="pe-profit-print-value hidden mt-3 text-sm text-black">
                    {unterschrift.bestaetigt
                        ? '✓ Bestätigt: Die Prognosewerte beruhen auf eigener Einschätzung.'
                        : 'Bestätigung ausstehend.'}
                </p>
            </section>
            )}

            {hasBankerPitch && (
                <BankerPitchReportSection
                    text={bankerPitchText}
                    onClear={() => void handleClearBankerPitch()}
                />
            )}
        </div>
    );
};

export default PEProfitReport;
